// Blog — list posts (blog_posts). Cover thumb, LV title, slug, status,
// published date. Row → editor. The storefront reads published posts via
// GET /api/v1/storefront/blog.

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Newspaper, Plus, Trash2, Loader2 } from "lucide-react";
import { supabase } from "@vinoga/db";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useT } from "@/lib/i18n";
import { mediaSrc } from "@/lib/media";

type BlogPostRow = {
  id: string;
  slug: string;
  translations: { lv?: { title?: string } } | null;
  cover_image_url: string | null;
  status: "draft" | "published";
  published_at: string | null;
  position: number;
};

export function Blog() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const tr = useT();
  const [rows, setRows] = useState<BlogPostRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("blog_posts")
      .select("id, slug, translations, cover_image_url, status, published_at, position")
      .order("position", { ascending: true });
    if (error) {
      toast({
        title: tr("blog.errorLoading", "Failed to load posts"),
        description: error.message,
        variant: "destructive",
      });
    } else {
      setRows((data as BlogPostRow[]) ?? []);
    }
    setLoading(false);
  }, [toast, tr]);

  useEffect(() => {
    void load();
  }, [load]);

  const remove = useCallback(
    async (id: string) => {
      if (!confirm(tr("blog.confirmDelete", "Delete this post?"))) return;
      const { error } = await supabase.from("blog_posts").delete().eq("id", id);
      if (error) {
        toast({ title: tr("common.deleteFailed", "Delete failed"), variant: "destructive" });
        return;
      }
      setRows((prev) => prev.filter((r) => r.id !== id));
    },
    [toast, tr],
  );

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 font-display text-xl font-bold text-foreground">
            <Newspaper size={20} className="text-primary" />
            {tr("blog.title", "Blog")}
          </h1>
          <p className="mt-1 font-body text-sm text-muted-foreground">
            {tr("blog.subtitle", "Posts served on the storefront under /blog.")}
          </p>
        </div>
        <Button size="sm" onClick={() => navigate("/blog/new")}>
          <Plus size={16} /> {tr("blog.new", "New post")}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
          <Loader2 className="animate-spin" size={16} /> {tr("common.loading", "Loading…")}
        </div>
      ) : rows.length === 0 ? (
        <p className="py-10 font-body text-sm text-muted-foreground">
          {tr("blog.empty", "No posts yet.")}
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          {rows.map((r) => {
            const thumb = mediaSrc(r.cover_image_url);
            return (
              <div
                key={r.id}
                className="flex cursor-pointer items-center gap-3 border-b border-border px-4 py-3 transition-colors last:border-b-0 hover:bg-muted/20"
                onClick={() => navigate(`/blog/${r.id}`)}
              >
                <div className="h-12 w-20 shrink-0 overflow-hidden rounded border border-border bg-muted/20">
                  {thumb ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={thumb}
                      alt={r.translations?.lv?.title ?? r.slug}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <Newspaper className="h-full w-full p-3 text-muted-foreground/40" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">
                    {r.translations?.lv?.title ?? tr("blog.untitled", "(untitled)")}
                  </p>
                  <p className="font-mono text-xs text-muted-foreground">/{r.slug}</p>
                </div>
                {r.published_at && (
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {new Date(r.published_at).toLocaleDateString("lv-LV")}
                  </span>
                )}
                <Badge variant={r.status === "published" ? "default" : "secondary"} className="shrink-0">
                  {r.status === "published"
                    ? tr("blog.statusPublished", "published")
                    : tr("blog.statusDraft", "draft")}
                </Badge>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={(e) => {
                    e.stopPropagation();
                    void remove(r.id);
                  }}
                >
                  <Trash2 size={14} className="text-muted-foreground" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
